import { handleResponse } from "@/services/handlers";
import {
  BACKEND_BASE_URL,
  ISolCustomerFaultParams,
  ISolCustomerFaultResponse,
  IOrdersService,
  IRequestHistorialParams,
  IRequestHistorialResponse,
  IResponseError,
  ISolCustomerResponse,
  ISolCustomerDenominationParams,
  ISolCustomerDenominationResponse,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
  ISolCustomerSupplyParams,
  ISolCustomerSupplyResponse,
  ISolCustomerGetServiceParams,
  ISolCustomerServiceRatingResponse,
  ISolCustomerGetPickUpServiceResponse,
  ISolCustomerGetAssistanceServiceResponse,
  ISolCustomerGetDeliveryServiceResponse,
  ISolCustomerGetSupplyMaterialServiceResponse,
  ISolCustomerCancellationReasonResponse,
  ISolCustomerOrderCancellationRequestBody,
  IRequestClientsReport,
} from "@/services";
import Vue from "vue";
import {
  IAssistanceService,
  IDeliveryService,
  IPickUpService,
  ISolCommodityCounter,
  ISolCustomerServiceRating,
  ISolCustomerSupplyMaterialService,
  IUserClientReportTypeData,
} from "@/entities";
import { APPLICATION_JSON } from "@/constans";

export class OrdersService implements IOrdersService {
  async getRequestHistorial(
    params: IRequestHistorialParams
  ): Promise<IRequestHistorialResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<IRequestHistorialResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/request-historial/${params.servicePointId}`,
        {
          headers,
          params,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async savePickUpService(
    pickUpService: IPickUpService
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<ISolCustomerResponse, IResponseError>(
      Vue.$axios.put(
        `${BACKEND_BASE_URL}/api/sol-customer/${pickUpService.countryCode}/v1/pick-up-service`,
        pickUpService,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getFaults(params: ISolCustomerFaultParams): Promise<ISolCustomerFaultResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerFaultResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/fault`, {
        headers,
        params,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async saveAssistanceService(
    assistanceService: IAssistanceService
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<ISolCustomerResponse, IResponseError>(
      Vue.$axios.put(
        `${BACKEND_BASE_URL}/api/sol-customer/${assistanceService.countryCode}/v1/assistance-service`,
        assistanceService,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getDenominations(
    params: ISolCustomerDenominationParams
  ): Promise<ISolCustomerDenominationResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerDenominationResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/denomination`, {
        headers,
        params,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getPacks(params: ISolCustomerPackParams): Promise<ISolCustomerPackResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerPackResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/pack`, {
        headers,
        params,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async saveDeliveryService(
    deliveryService: IDeliveryService
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<ISolCustomerResponse, IResponseError>(
      Vue.$axios.put(
        `${BACKEND_BASE_URL}/api/sol-customer/${deliveryService.countryCode}/v1/deliver-service`,
        deliveryService,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getSupply(
    params: ISolCustomerSupplyParams
  ): Promise<ISolCustomerSupplyResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerSupplyResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/supply`, {
        headers,
        params,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async saveSupplyMaterialService(
    supplyMaterialService: ISolCustomerSupplyMaterialService
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<ISolCustomerResponse, IResponseError>(
      Vue.$axios.put(
        `${BACKEND_BASE_URL}/api/sol-customer/${supplyMaterialService.countryCode}/v1/supply-material-service`,
        supplyMaterialService,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async saveServiceRating(
    serviceRating: ISolCustomerServiceRating
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<ISolCustomerResponse, IResponseError>(
      Vue.$axios.put(
        `${BACKEND_BASE_URL}/api/sol-customer/${serviceRating.countryCode}/v1/service-rating`,
        serviceRating,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getServiceRating(
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerServiceRatingResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerServiceRatingResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/service-rating/${params.serviceId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getPickUpService(
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetPickUpServiceResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerGetPickUpServiceResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/pick-up-service/${params.serviceId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getAssistanceService(
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetAssistanceServiceResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerGetAssistanceServiceResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/assistance-service/${params.serviceId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getDeliveryService(
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetDeliveryServiceResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerGetDeliveryServiceResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/deliver-service/${params.serviceId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getSupplyMaterialService(
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/sol-customer/${params.countryCode}/v1/supply-material-service/${params.serviceId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getCancellationReasonsService(
    countryCode: string
  ): Promise<ISolCustomerCancellationReasonResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerCancellationReasonResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/cancellation-reason`, {
        headers,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async patchCancelPickupService(
    countryCode: string,
    serviceId: string,
    body: ISolCustomerOrderCancellationRequestBody
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.patch(
        `${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/pick-up-service/${serviceId}`,
        body,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async patchCancelDeliveryService(
    countryCode: string,
    serviceId: string,
    body: ISolCustomerOrderCancellationRequestBody
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.patch(
        `${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/deliver-service/${serviceId}`,
        body,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async patchCancelDeliveryExchangeService(
    countryCode: string,
    serviceId: string,
    body: ISolCustomerOrderCancellationRequestBody
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.patch(
        `${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/deliver-exchange-service/${serviceId}`,
        body,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async patchCancelSupplyMaterialService(
    countryCode: string,
    serviceId: string,
    body: ISolCustomerOrderCancellationRequestBody
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.patch(
        `${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/supply-material-service/${serviceId}`,
        body,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async patchCancelAssistanceService(
    countryCode: string,
    serviceId: string,
    body: ISolCustomerOrderCancellationRequestBody
  ): Promise<ISolCustomerResponse | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<ISolCustomerResponse | undefined>,
      IResponseError
    >(
      Vue.$axios.patch(
        `${BACKEND_BASE_URL}/api/sol-customer/${countryCode}/v1/assistance-service/${serviceId}`,
        body,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async postGenerateOrdersReport(request?: IRequestClientsReport): Promise<void> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    await handleResponse<Promise<void>, IResponseError>(
      Vue.$axios.post(`${BACKEND_BASE_URL}/api/configuration/v1/report/orders`, request, {
        headers,
      })
    );
  }

  async getTypeClientsReport(): Promise<IUserClientReportTypeData[] | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };

    const { response, error } = await handleResponse<
      Promise<IUserClientReportTypeData[] | undefined>,
      IResponseError
    >(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/configuration/v1/report/orders/types`, {
        headers,
      })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getSupplyCounters(servicePointId: number): Promise<ISolCommodityCounter[] | undefined> {
    const headers: Record<string, string> = {
      Accept: APPLICATION_JSON,
    };
    const { response, error } = await handleResponse<
      Promise<ISolCommodityCounter[] | undefined>,
      IResponseError
    >(
      Vue.$axios.get(
        `${BACKEND_BASE_URL}/api/configuration/v1/material-counter/${servicePointId}`,
        {
          headers,
        }
      )
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }
}
